<template>
  <v-container fluid>
  <v-row>
    <v-col cols="12">
        <material-card
          color="primary"
          full-width
        >
        <template #header>
          <v-row align="center">
            <v-col>
              <p
                class="headline font-weight-regular mb-2"
                v-text="$t('companyConfiguration')"
              />
          </v-col>
          <v-col cols="auto">
            <CompanyLogo
              :companyInfo="companyInfo"/>
          </v-col>
        </v-row>
        </template>
          <v-container fluid>
            <v-row v-if="companyInfo">
              <v-col cols="12" v-if="showSaveChanges">
                <v-layout wrap>
                  <v-btn data-testid="cancel-company-information" class="cancel" color="black" text @click="reset">{{$t('cancel')}}</v-btn>
                  <v-spacer/>
                  <v-btn data-testid="save-company-information" class="primary" @click="updateInfo">{{$t('saveChanges')}}</v-btn>
                </v-layout>
              </v-col>
              </v-row>
              <v-row>
                <v-col cols="12"><span class="title">{{$t('administrativeInformation')}}</span></v-col>
                <v-col cols="12" sm="6" lg="4">
                  <LoadableComponent :loading="loading">
                    <v-text-field
                    v-model="companyInfo.code"
                    :label="$t('code')"
                    data-testid="company-info-code"
                    color="primary"
                    outlined
                    readonly
                    />
                  </LoadableComponent>
                </v-col>
                <v-col cols="12" xs="12" sm="6" lg="4">
                  <LoadableComponent :loading="loading">
                    <v-text-field
                    v-model="companyInfo.name"
                    :label="$t('name')"
                    data-testid="company-info-name"
                    color="primary"
                    readonly
                    outlined
                    />
                  </LoadableComponent>
                </v-col>
                <v-col cols="12" xs="12" sm="6" lg="4">
                  <LoadableComponent :loading="loading">
                    <v-text-field
                    v-model="companyInfo.tenantIdGuid"
                    :label="$t('tenantId')"
                    data-testid="company-info-tenant-id-guid"
                    color="primary"
                    readonly
                    outlined
                  />
                  </LoadableComponent>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="6">
                  <v-row>
                    <v-col align="left">
                      <span class="title">{{$t('options')}}</span>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col>
                      <CompanyOptions
                      :readonly="fieldsAreReadonly"
                      :config="config"
                      :customConnections="biConnections"
                      :customMapLayersConnection="customMapLayersConnection"
                      @config-changed="config = $event"
                      @blob-config-changed="onBlobConfigChanged"
                      :samsaraHalted="companyInfo?.samsaraIntegrationHalted ?? false"
                      :loading="loading"
                      :unsavedChanges="showSaveChanges"
                      @refresh-companyInfo="getCompanyInfo"/>
                    </v-col>
                  </v-row>
                </v-col>
                <v-col cols="6">
                  <v-row>
                    <v-col align="right">
                      <span class="title" style="text">{{$t('addressAndContacts')}}</span>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col>
                      <CompanyContactInfo
                      class="mb-4"
                      :mainContact="mainContact"
                      :transporterContact="transporterContact"
                      :loaderContact="loaderContact"
                      :insuranceContact="insuranceContact"
                      :settlementContact="settlementContact"
                      :readonly="fieldsAreReadonly"
                      :address="address"
                      :loading="loading"
                      @deleteTransporterContact="deleteTransporterContact"
                      @deleteLoaderContact="deleteLoaderContact"
                      @loaderSet="loaderContact = $event"
                      @transporterSet="transporterContact = $event"
                      @mainEdited="mainContact = $event"
                      @addressUpdated="address = $event"
                      @insuranceEdited="insuranceContact = $event"
                      @settlementEdited="settlementContact = $event"/>
                    </v-col>
                  </v-row>
                </v-col>
                <v-col cols="12">
                  <CompanyEntities
                  v-if="businessEntities"
                  :companyInfo="companyInfo"
                  @entity-mutated="getEntities"/>
                </v-col>
              </v-row>
              <v-dialog v-model="addDeckNowDialog" max-width="400">
                <ConfirmDialog
                  v-if="addDeckNowDialog"
                  :title="$t('addDeckNowTitle')"
                  :body="$t('addDeckNowBody')"
                  :confirmText="$t('addDeck')"
                  color="primary"
                  @confirm="showDeckForm"
                  @cancel="closeConfirmDialog"
                />
              </v-dialog>
            </v-container>
        </material-card>
    </v-col>
  </v-row>
  </v-container>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { FinancialIntegrationType, AdjustingEntriesFrequency, UserClaims, SystemDayOfWeek, SamsaraIntegrationStatus, StorageReplicationMode, SettlementEmailMessageMode, EndangeredSpeciesMode, DueDiligenceApprovalMode, SettlementReportMode, CustomConnectionType } from '@/utils/Enumerations.js'
import { states } from '@/utils/constants.js'
import { mask } from 'vue-the-mask'
import isEqual from 'lodash/isEqual'
import timezones from '@/components/settings-components/company-config/timezones.json'
import moment from 'moment'
import { userAssignedClaim } from '@/utils/ClaimUtility'
import { isMobile } from '@/utils/DeviceUtility.js'
import cloneDeep from 'lodash/cloneDeep'

const PRODUCT_CODES = 'productCodes'
const BYPRODUCT_CODES = 'byproductCodes'
const EXPORT_CODES = 'exportCodes'
const GL_CODES = 'glCodes'
const ALL = 'all'

export default {
  name: 'CompanyInformation',

  components: {
    ConfirmDialog: () => import('@/components/helper/ConfirmDialog.vue'),
    CompanyEntities: () => import('@/components/settings-components/company-config/CompanyEntities.vue'),
    CompanyLogo: () => import('@/components/settings-components/company-config/CompanyLogo.vue'),
    CompanyContactInfo: () => import('@/components/settings-components/company-config/CompanyContactInfo.vue'),
    CompanyOptions: () => import('@/components/settings-components/company-config/CompanyOptions.vue'),
    LoadableComponent: () => import('@/components/helper/LoadableComponent.vue')
  },

  data: () => ({
    companyInfo: {
      address: {},
      code: '',
      name: '',
      shortName: '',
      financialIntegrationType: FinancialIntegrationType.enums[0]
    },
    mainContact: {
      firstName: '',
      lastName: '',
      phoneNumber: '',
      email: ''
    },
    transporterContact: {
      firstName: '',
      lastName: '',
      phoneNumber: '',
      email: ''
    },
    loaderContact: {
      firstName: '',
      lastName: '',
      phoneNumber: '',
      email: ''
    },
    insuranceContact: {
      firstName: '',
      lastName: '',
      phoneNumber: '',
      email: ''
    },
    settlementContact: {
      firstName: '',
      lastName: '',
      phoneNumber: '',
      email: ''
    },
    config: {
      financialIntegrationType: FinancialIntegrationType.enums[0],
      adjustingEntriesFrequency: undefined,
      biReplicationStorageMode: StorageReplicationMode.None,
      settlementEmailMessageMode: SettlementEmailMessageMode.None,
      endangeredSpeciesMode: EndangeredSpeciesMode.None,
      settlementReportMode: SettlementReportMode.Standard,
      defaultSettlementMessage: '',
      localDailyTicketReportHour: 0,
      requireApprovalForContractModifications: false,
      dueDiligenceApprovalMode: DueDiligenceApprovalMode.None,
      filterContractsToLoaderAccount: false,
      lastDailyTicketReportCreatedAt: undefined,
      accountPayableTemplateDayOfWeek: 0,
      accountPayableTemplateLocalHour: 0,
      samsaraIntegration: false,
      useCustomMapLayers: false,
      shortName: ''
    },
    customConnections: [],
    blobStorageConfig: [],
    blobConfigMutated: false,
    samsaraConfig: undefined,
    deleteSamsara: false,
    loading: false,
    addDeckNowDialog: false,
    deckForm: false,
    financialIntegrationType: FinancialIntegrationType.enums[0],
    phoneMask: '(###)-###-####',
    locationId: -1,
    address: {
      addressLine1: '',
      addressLine2: ''
    },
    locations: [],
    states,
    timezones,
    UserClaims,
    SystemDayOfWeek
  }),

  directives: {
    mask
  },

  watch: {
    'config.samsaraIntegration' (val) {
      if (val) return
      this.deleteSamsara = true
    }
  },

  async created () {
    await this.getCompanyInfo()
    await this.getEntities()
  },

  computed: {
    ...mapGetters('user', ['businessEntities']),
    ...mapGetters('samsara', ['samsaraConfigurationMutated', 'samsaraConfiguration', 'samsaraConfigurationMutated']),
    showSaveChanges () {
      const timezoneMutated = Number(this.config.selectedTimeZone?.offset) !== this.companyInfo.timeZoneOffset
      const mainContactMutated = !isEqual(this.mainContact, this.companyInfo.mainContact)
      const insuranceContactMutated = !isEqual(this.insuranceContact, this.companyInfo.insuranceContact) &&
        !(this.checkForBlankContact(this.insuranceContact) === undefined && this.companyInfo.insuranceContact === null)
      const settlementContactMutated = !isEqual(this.settlementContact, this.companyInfo.settlementContact) &&
        !(this.checkForBlankContact(this.settlementContact) === undefined && this.companyInfo.settlementContact === null)
      const transporterContactMutated = !isEqual(this.transporterContact, this.companyInfo.transporterContact) &&
        !(this.checkForBlankContact(this.transporterContact) === undefined && this.companyInfo.transporterContact === null)
      const loaderContactMutated = !isEqual(this.loaderContact, this.companyInfo.loaderContact) &&
        !(this.checkForBlankContact(this.loaderContact) === undefined && this.companyInfo.loaderContact === null)
      const addressMutated = !isEqual(this.address, this.companyInfo.address)
      const financialIntegrationTypeMutated = !isEqual(this.config.financialIntegrationType, this.companyInfo.financialIntegrationType)
      const adjustingEntriesFrequencyMutated = this.config.adjustingEntriesFrequency?.value !== this.companyInfo.adjustingEntriesFrequency?.value
      const biReplicationStorageModeMutated = this.config.biReplicationStorageMode?.value !== this.companyInfo.biReplicationStorageMode
      const settlementEmailMessageModeMutated = this.config.settlementEmailMessageMode?.value !== this.companyInfo.settlementEmailMessageMode
      const endangeredSpeciesModeMutated = this.config.endangeredSpeciesMode?.value !== this.companyInfo.endangeredSpeciesMode
      const settlementReportModeMutated = this.config.settlementReportMode?.value !== this.companyInfo.settlementReportMode
      const defaultSettlementMessageMutated = (this.config.defaultSettlementMessage ?? '') !== (this.companyInfo.defaultSettlementMessage ?? '')
      const requireApprovalForContractModificationsMutated = this.config.requireApprovalForContractModifications !== this.companyInfo.requireApprovalForContractModifications
      const dueDiligenceApprovalModeMutated = this.config.dueDiligenceApprovalMode?.value !== this.companyInfo.dueDiligenceApprovalMode
      const localDailyTicketReportHourMutated = this.config.localDailyTicketReportHour !== this.companyInfo.localDailyTicketReportHour
      const accountPaymentDayMutated = this.config.accountPayableTemplateDayOfWeek?.value !== this.companyInfo.recurringAccountPaymentDayOfWeek
      const accountPaymentTimeMutated = this.config.accountPayableTemplateLocalHour !== this.companyInfo.recurringAccountPaymentHour
      const integrateWithSamsaraMutated = this.config.samsaraIntegration !== this.integratesWithSamsara
      const useCustomMapLayersMutated = this.config.useCustomMapLayers !== (this.customMapLayersConnection !== null)
      const filterContractsToLoaderAccountMutated = this.config.filterContractsToLoaderAccount !== this.companyInfo.filterContractsToLoaderAccount
      const shortNameMutated = !isEqual(this.config.shortName, this.companyInfo.shortName)

      return mainContactMutated ||
        loaderContactMutated ||
        transporterContactMutated ||
        insuranceContactMutated ||
        settlementContactMutated ||
        addressMutated ||
        timezoneMutated ||
        financialIntegrationTypeMutated ||
        adjustingEntriesFrequencyMutated ||
        biReplicationStorageModeMutated ||
        settlementEmailMessageModeMutated ||
        endangeredSpeciesModeMutated ||
        settlementReportModeMutated ||
        defaultSettlementMessageMutated ||
        requireApprovalForContractModificationsMutated ||
        dueDiligenceApprovalModeMutated ||
        localDailyTicketReportHourMutated ||
        accountPaymentDayMutated ||
        accountPaymentTimeMutated ||
        integrateWithSamsaraMutated ||
        useCustomMapLayersMutated ||
        this.samsaraConfigurationMutated ||
        filterContractsToLoaderAccountMutated ||
        shortNameMutated ||
        this.blobConfigMutated
    },

    fieldsAreReadonly () {
      return !userAssignedClaim(UserClaims.GlobalAdmin)
    },

    biConnections () {
      return this.customConnections.filter(cc => cc.connectionType === CustomConnectionType.BiReplication.value)
    },

    customMapLayersConnection () {
      return this.customConnections.find(cc => cc.connectionType === CustomConnectionType.CustomMapLayers.value) ?? null
    },

    enableDownloadIcon () {
      return this.companyInfo.financialIntegrationType.value !== FinancialIntegrationType.None.value
    },

    CSVDownloadOptions () {
      return [PRODUCT_CODES, BYPRODUCT_CODES, EXPORT_CODES, GL_CODES, ALL]
    },

    samsaraRequest () {
      const request = { ...this.samsaraConfiguration }
      request.samsaraContactId = request.samsaraContact.applicationUserId
      request.integrationStatus ??= SamsaraIntegrationStatus.Active.value
      return request
    },

    integratesWithSamsara () {
      return !(this.companyInfo.samsaraIntegrationStatus == null || this.companyInfo.samsaraIntegrationStatus === SamsaraIntegrationStatus.NotConfigured.value)
    }
  },

  methods: {
    ...mapActions('user', ['fetchCompanyInfo', 'updateCompanyInfo', 'fetchAllBusinessEntities', 'fetchCustomConnections', 'removeCustomLayersConnection']),
    ...mapActions('product', ['fetchProducts']),
    ...mapActions('account', ['fetchAccounts']),
    ...mapActions('activity-templates', ['fetchActivityTemplates']),
    ...mapActions('samsara', [
      'createSamsaraConfiguration',
      'updateSamsaraConfiguration',
      'deleteSamsaraConfiguration',
      'fetchSamsaraConfiguration',
      'resetSamsaraConfiguration'
    ]),
    userAssignedClaim,
    isMobile,
    moment,

    async getCompanyInfo () {
      this.loading = true
      try {
        const isGlobalAdmin = userAssignedClaim(UserClaims.GlobalAdmin)
        const [info, customConnections] = await Promise.all([
          this.fetchCompanyInfo(),
          isGlobalAdmin ? this.fetchCustomConnections() : Promise.resolve([])
        ])
        this.companyInfo = { ...info }
        this.customConnections = customConnections ?? []
        const tz = (this.timezones.find(tz => Number(tz.offset) === this.companyInfo.timeZoneOffset))
        this.config.selectedTimeZone = tz

        this.companyInfo.financialIntegrationType = FinancialIntegrationType.forInt(info.financialIntegrationType)
        this.companyInfo.adjustingEntriesFrequency = AdjustingEntriesFrequency.forInt(info.adjustingEntriesFrequency)

        this.config.adjustingEntriesFrequency = AdjustingEntriesFrequency.forInt(info.adjustingEntriesFrequency)

        this.config.financialIntegrationType = this.companyInfo.financialIntegrationType
        this.config.biReplicationStorageMode = StorageReplicationMode.forInt(info.biReplicationStorageMode) ?? StorageReplicationMode.None
        this.config.settlementEmailMessageMode = SettlementEmailMessageMode.forInt(info.settlementEmailMessageMode) ?? SettlementEmailMessageMode.None
        this.config.endangeredSpeciesMode = EndangeredSpeciesMode.forInt(info.endangeredSpeciesMode) ?? EndangeredSpeciesMode.None
        this.config.settlementReportMode = SettlementReportMode.forInt(info.settlementReportMode) ?? SettlementReportMode.Standard
        this.config.defaultSettlementMessage = info.defaultSettlementMessage ?? ''
        this.config.requireApprovalForContractModifications = info.requireApprovalForContractModifications
        this.config.dueDiligenceApprovalMode = DueDiligenceApprovalMode.forInt(info.dueDiligenceApprovalMode) ?? DueDiligenceApprovalMode.None
        this.config.filterContractsToLoaderAccount = this.companyInfo.filterContractsToLoaderAccount
        this.config.localDailyTicketReportHour = this.companyInfo.localDailyTicketReportHour
        this.config.lastDailyTicketReportCreatedAt = this.companyInfo.lastDailyTicketReportCreatedAt
        this.config.samsaraIntegration = this.integratesWithSamsara
        this.config.useCustomMapLayers = this.customMapLayersConnection !== null
        this.config.accountPayableTemplateDayOfWeek = SystemDayOfWeek.forInt(info.recurringAccountPaymentDayOfWeek)
        this.config.accountPayableTemplateLocalHour = info.recurringAccountPaymentHour
        this.reset()
      } finally {
        this.loading = false
      }
    },

    async getEntities () {
      this.loading = true
      this.deckForm = false
      this.addDeckNowDialog = false
      this.locationId = -1
      await this.fetchAllBusinessEntities()
      this.loading = false
    },

    checkForBlankContact (contact) {
      if (contact?.email === '' &&
        contact?.firstName === '' &&
        contact?.lastName === '' &&
        contact?.phoneNumber === '') return undefined
      return contact
    },

    deleteTransporterContact () {
      this.transporterContact = {
        firstName: '',
        lastName: '',
        phoneNumber: '',
        email: ''
      }
    },

    deleteLoaderContact () {
      this.loaderContact = {
        firstName: '',
        lastName: '',
        phoneNumber: '',
        email: ''
      }
    },

    closeConfirmDialog () {
      this.locationId = -1
      this.addDeckNowDialog = false
    },

    showDeckForm () {
      this.addDeckNowDialog = false
      this.deckForm = true
    },

    onBlobConfigChanged (blobConfig) {
      this.blobStorageConfig = blobConfig
      this.blobConfigMutated = true
    },

    async updateInfo () {
      if (!this.verifyRequest()) {
        return
      }
      const updateRequest = this.getUpdateRequest()
      await this.updateCompanyInfo(updateRequest)

      if (!this.config.useCustomMapLayers && this.customMapLayersConnection) {
        await this.removeCustomLayersConnection()
      }

      let refreshSamsara = false
      if (this.config.samsaraIntegration === false && this.integratesWithSamsara) {
        await this.deleteSamsaraConfiguration()
      } else if (this.integratesWithSamsara && this.samsaraConfigurationMutated) {
        await this.updateSamsaraConfiguration(this.samsaraRequest)
        refreshSamsara = true
      } else if (this.samsaraConfigurationMutated) {
        await this.createSamsaraConfiguration(this.samsaraRequest)
        refreshSamsara = true
      }

      await this.getCompanyInfo()
      if (!refreshSamsara) return

      await this.fetchSamsaraConfiguration()
    },

    reset () {
      if (this.companyInfo.mainContact) {
        this.mainContact = cloneDeep(this.companyInfo.mainContact)
      } else {
        this.mainContact = {
          firstName: '',
          lastName: '',
          phoneNumber: '',
          email: ''
        }
      }

      if (this.companyInfo.transporterContact) {
        this.transporterContact = cloneDeep(this.companyInfo.transporterContact)
      } else {
        this.transporterContact = {
          firstName: '',
          lastName: '',
          phoneNumber: '',
          email: ''
        }
      }

      if (this.companyInfo.loaderContact) {
        this.loaderContact = cloneDeep(this.companyInfo.loaderContact)
      } else {
        this.loaderContact = {
          firstName: '',
          lastName: '',
          phoneNumber: '',
          email: ''
        }
      }

      if (this.companyInfo.insuranceContact) {
        this.insuranceContact = cloneDeep(this.companyInfo.insuranceContact)
      } else {
        this.insuranceContact = {
          firstName: '',
          lastName: '',
          phoneNumber: '',
          email: ''
        }
      }

      if (this.companyInfo.settlementContact) {
        this.settlementContact = cloneDeep(this.companyInfo.settlementContact)
      } else {
        this.settlementContact = {
          firstName: '',
          lastName: '',
          phoneNumber: '',
          email: ''
        }
      }

      if (this.companyInfo.address) {
        this.address = cloneDeep(this.companyInfo.address)
      } else {
        this.address = {
          addressLine1: '',
          addressLine2: '',
          type: 0,
          city: '',
          state: '',
          postalCode: ''
        }
      }

      this.config.financialIntegrationType = this.companyInfo.financialIntegrationType
      this.config.requireApprovalForContractModifications = this.companyInfo.requireApprovalForContractModifications
      this.config.dueDiligenceApprovalMode = DueDiligenceApprovalMode.forInt(this.companyInfo.dueDiligenceApprovalMode) ?? DueDiligenceApprovalMode.None
      this.config.filterContractsToLoaderAccount = this.companyInfo.filterContractsToLoaderAccount
      this.config.biReplicationStorageMode = StorageReplicationMode.forInt(this.companyInfo.biReplicationStorageMode) ?? StorageReplicationMode.None
      this.config.settlementEmailMessageMode = SettlementEmailMessageMode.forInt(this.companyInfo.settlementEmailMessageMode) ?? SettlementEmailMessageMode.None
      this.config.endangeredSpeciesMode = EndangeredSpeciesMode.forInt(this.companyInfo.endangeredSpeciesMode) ?? EndangeredSpeciesMode.None
      this.config.settlementReportMode = SettlementReportMode.forInt(this.companyInfo.settlementReportMode) ?? SettlementReportMode.Standard
      this.config.defaultSettlementMessage = this.companyInfo.defaultSettlementMessage ?? ''
      this.config.samsaraIntegration = this.integratesWithSamsara
      this.config.useCustomMapLayers = this.customMapLayersConnection !== null
      this.config.shortName = this.companyInfo.shortName
      this.blobStorageConfig = []
      this.blobConfigMutated = false

      if (this.config.adjustingEntriesFrequency) {
        this.config.adjustingEntriesFrequency = this.companyInfo.adjustingEntriesFrequency
      } else {
        this.config.adjustingEntriesFrequency = undefined
      }

      const tz = (this.timezones.find(tz => Number(tz.offset) === this.companyInfo.timeZoneOffset))
      if (tz) {
        this.config.selectedTimeZone = tz
      }

      this.resetSamsaraConfiguration()
    },

    getUpdateRequest () {
      this.mainContact.personId = 0
      this.transporterContact.personId = 0
      this.loaderContact.personId = 0
      this.insuranceContact.personId = 0
      this.settlementContact.personId = 0
      this.address.addressId = 0

      const formattedContact = cloneDeep(this.mainContact)
      let formattedTransporterContact = cloneDeep(this.transporterContact)
      let formattedLoaderContact = cloneDeep(this.loaderContact)
      let formattedInsuranceContact = cloneDeep(this.insuranceContact)
      let formattedSettlementContact = cloneDeep(this.settlementContact)
      formattedContact.phoneNumber.replace('(', '').replace(')', '').replace('-', '').replace('-', '')
      formattedTransporterContact.phoneNumber.replace('(', '').replace(')', '').replace('-', '').replace('-', '')
      formattedLoaderContact.phoneNumber.replace('(', '').replace(')', '').replace('-', '').replace('-', '')
      formattedInsuranceContact.phoneNumber.replace('(', '').replace(')', '').replace('-', '').replace('-', '')

      if (formattedTransporterContact.email === '' &&
        formattedTransporterContact.firstName === '' &&
        formattedTransporterContact.lastName === '' &&
        formattedTransporterContact.phoneNumber === '') formattedTransporterContact = null

      if (formattedLoaderContact.email === '' &&
        formattedLoaderContact.firstName === '' &&
        formattedLoaderContact.lastName === '' &&
        formattedLoaderContact.phoneNumber === '') formattedLoaderContact = null

      if (formattedInsuranceContact.email === '' &&
        formattedInsuranceContact.firstName === '' &&
        formattedInsuranceContact.lastName === '' &&
        formattedInsuranceContact.phoneNumber === '') formattedInsuranceContact = null

      if (formattedSettlementContact.email === '' &&
        formattedSettlementContact.firstName === '' &&
        formattedSettlementContact.lastName === '' &&
        formattedSettlementContact.phoneNumber === '') formattedSettlementContact = null

      return {
        mainContact: formattedContact,
        transporterContact: formattedTransporterContact,
        loaderContact: formattedLoaderContact,
        insuranceContact: formattedInsuranceContact,
        settlementContact: formattedSettlementContact,
        financialIntegrationType: this.config.financialIntegrationType.value,
        address: this.address,
        timeZoneOffset: Number(this.config.selectedTimeZone.offset),
        adjustingEntriesFrequency: this.config.adjustingEntriesFrequency.value,
        biReplicationStorageMode: this.config.biReplicationStorageMode?.value ?? StorageReplicationMode.None.value,
        settlementEmailMessageMode: this.config.settlementEmailMessageMode?.value ?? SettlementEmailMessageMode.None.value,
        endangeredSpeciesMode: this.config.endangeredSpeciesMode?.value ?? EndangeredSpeciesMode.None.value,
        settlementReportMode: this.config.settlementReportMode?.value ?? SettlementReportMode.Standard.value,
        defaultSettlementMessage: this.config.defaultSettlementMessage ? this.config.defaultSettlementMessage : null,
        requireApprovalForContractModifications: this.config.requireApprovalForContractModifications,
        // The server refuses a non-None due diligence mode paired with a None species mode, so never let the two disagree
        dueDiligenceApprovalMode: this.config.endangeredSpeciesMode?.value === EndangeredSpeciesMode.None.value
          ? DueDiligenceApprovalMode.None.value
          : this.config.dueDiligenceApprovalMode?.value ?? DueDiligenceApprovalMode.None.value,
        filterContractsToLoaderAccount: this.config.filterContractsToLoaderAccount,
        localDailyTicketReportHour: this.config.localDailyTicketReportHour,
        recurringAccountPaymentHour: this.config.accountPayableTemplateLocalHour,
        recurringAccountPaymentDayOfWeek: this.config.accountPayableTemplateDayOfWeek.value,
        shortName: this.config.shortName ? this.config.shortName : null,
        ...(this.blobConfigMutated && {
          customConnections: this.blobStorageConfig.map(c => ({ name: c.name, connectionString: c.connectionString, customConnectionId: c.customConnectionId ?? null }))
        })
      }
    },

    verifyRequest () {
      if (this.address.addressLine1 === '' ||
      this.address.city === '' ||
      this.address.postalCode === '' ||
      this.address.state === '') {
        this.setSnackError(this.$t('fillAddress'))
        return false
      }

      const contactMissingField = (contact) => {
        return (contact.firstName === '' ||
          contact.lastName === '' ||
          contact.phoneNumber === '' ||
          contact.email === ''
        )
      }

      if (contactMissingField(this.mainContact)) {
        this.setSnackError(this.$t('fillMainContact'))
        return false
      }

      if (this.config.samsaraIntegration &&
          (this.samsaraConfiguration.trailerIdPrefix === '' ||
          contactMissingField(this.samsaraConfiguration.samsaraContact))
      ) {
        this.setSnackError(this.$t('fillSamsaraConfiguration'))
        return false
      }

      const wasNotBlobMode = this.companyInfo.biReplicationStorageMode !== StorageReplicationMode.BlobStorage.value
      const isNowBlobMode = this.config.biReplicationStorageMode?.value === StorageReplicationMode.BlobStorage.value
      const transitioningToBlobMode = wasNotBlobMode && isNowBlobMode
      const hasAnyConnectionString = this.blobStorageConfig.some(c => c.connectionString)
      if (transitioningToBlobMode && !hasAnyConnectionString) {
        this.setSnackError(this.$t('fillBlobStorageConfiguration'))
        return false
      }

      if (!!this.companyInfo.shortName && !this.config.shortName) {
        this.setSnackError(this.$t('shortNameRequired'))
        return false
      }

      if (this.config.settlementEmailMessageMode?.value === SettlementEmailMessageMode.Default.value &&
          !this.config.defaultSettlementMessage?.trim()) {
        this.setSnackError(this.$t('defaultSettlementMessageRequired'))
        return false
      }

      return true
    }
  }

}
</script>
